<script lang="ts" setup>
import InstanceList from "@/components/business/routines/InstanceList.vue";
import RoutineList from "@/components/business/routines/RoutineList.vue";
import FiltersBar from "@/components/ui/FiltersBar.vue";
import LoadingOverlay from "@/components/ui/LoadingOverlay.vue";
import RoutePage from "@/components/ui/RoutePage.vue";
import { useRoutinePermissions } from "@/composables/permissions/useRoutinePermissions";
import {
  useCallRefreshToastError,
  useCallRefreshToastSuccess,
} from "@/composables/useCallRefreshToast";
import { useI18n } from "@/composables/useI18n";
import { useStartInstance } from "@/composables/useStartInstance";
import { useUsers } from "@/composables/useUsers";
import { ApiClient } from "@/services/clients";
import { useConfigStore } from "@/store/config";
import { useExecutionsStore } from "@/store/executions";
import { useFabriqStore } from "@/store/fabriq";
import { useInstancesStore } from "@/store/instances";
import { useRoutinesStore } from "@/store/routines";
import { useTeamsStore } from "@/store/teams";
import { useTemplatesStore } from "@/store/templates";
import { FilledInstance, Instance, Routine, Team, TemplateStep } from "@/types";
import { BarcodeScannerUI } from "@/utils/barcode-scanner-ui";
import { localLogger } from "@/utils/localLogger";
import mixpanelTracker from "@/utils/mixpanel-tracker";
import { useTransformLegacyPermalinks } from "@/utils/use_transform_legacy_permalinks";
import {
  BarcodeFormat,
  BarcodeScanner,
} from "@capacitor-mlkit/barcode-scanning";
import { CapacitorHttp as Http, HttpResponse } from "@capacitor/core";
import {
  IonInput,
  IonToolbar,
  onIonViewDidEnter,
  onIonViewDidLeave,
  RefresherEventDetail,
  toastController,
} from "@ionic/vue";
import { addDays, endOfWeek, format, startOfWeek, subDays } from "date-fns";
import { storeToRefs } from "pinia";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onDeactivated,
  ref,
  watch,
} from "vue";

const fabriqStore = useFabriqStore();
const routinesStore = useRoutinesStore();
const instancesStore = useInstancesStore();
const templatesStore = useTemplatesStore();
const teamsStore = useTeamsStore();
const configStore = useConfigStore();
const callRefreshToastSuccess = useCallRefreshToastSuccess(fabriqStore.theme);
const callRefreshToastError = useCallRefreshToastError();
const { can } = useRoutinePermissions();

const search = ref("");
let scanning = false;

const { t } = useI18n();

const hasEvLoader = ref(false);

const executionsStore = useExecutionsStore();
const { online, requests } = storeToRefs(fabriqStore);

const filter = ref("scheduled");
const filters = ["scheduled", "favorite", "routines"];

const barcodeUIBuilder = new BarcodeScannerUI();

const { loading, loadingText, startScheduledInstance, startRoutine } =
  useStartInstance();

const loadAllTeamRoutines = async () => {
  await routinesStore.loadAll();
};

const isSending = computed(() => {
  return instancesStore.collection.some((i: Instance) => i.sending);
});

watch(isSending, (v, o) => {
  if (!v && o) {
    loadAllData();
  }
});
let routinesLoaded = false;

const loadAllData = async (refresher?: RefresherEventDetail) => {
  const userId = fabriqStore.user?.id;
  if (!userId) return;
  if (!refresher && routinesLoaded && filter.value !== "scheduled") return;
  const today = new Date();
  const start_date = format(
    subDays(startOfWeek(today, { weekStartsOn: 1 }), 5),
    "yyyy-MM-dd"
  );
  const end_date = format(
    addDays(endOfWeek(today, { weekStartsOn: 1 }), 5),
    "yyyy-MM-dd"
  );

  const load = async () => {
    if (filter.value === "scheduled") {
      if (isSending.value) return;
      await instancesStore.all({
        start_date,
        end_date,
        id: userId,
        flush: true,
      });
    } else {
      await loadAllTeamRoutines();
    }
  };

  try {
    if (filter.value !== "scheduled") {
      routinesLoaded = true;
    }
    await nextTick();
    if (refresher) {
      hasEvLoader.value = true;
      nextTick(async () => {
        await load();
        refresher?.complete();
        hasEvLoader.value = false;
        await callRefreshToastSuccess(t("common.refreshedContent"));
      });
    } else {
      hasEvLoader.value = false;
      load();
    }
  } catch (error) {
    console.error(error);
    await callRefreshToastError(t("common.refreshedContentError"));
    loading.value = false;
  }
};

const favorites = computed(() => configStore.config?.favoriteRoutines || []);

const toggleFavorite = (id: number) => {
  if (favorites.value.includes(id)) {
    configStore.removeFavoriteRoutine(id);
  } else {
    configStore.addFavoriteRoutine(id);
  }
};

const instances = computed(() => {
  const userId = fabriqStore.user?.id;
  if (!userId) return;
  const instances: Array<FilledInstance> = [];
  instancesStore.collection.forEach((i: Instance) => {
    if (!i?.users?.includes(userId)) return;
    const routine = routinesStore.collection.find(
      (r: Routine) => r.id === i.routine_id
    );
    if (!routine || routine.is_archived) return;
    const templateId = i.template_id || routine.template_id;
    const template = templatesStore.collection.find(
      (r: Routine) => r.id === templateId
    );
    const execution = executionsStore.collection.find(
      (e: any) => e.instance === i.id
    );
    const hasKpiStep =
      template?.steps.some(
        (step: TemplateStep) => step.step_type === "indicator"
      ) || false;
    const isDisabledOnOffline =
      !template || typeof i.id !== "number" || !execution || hasKpiStep;
    const disabled = online.value ? false : isDisabledOnOffline;
    const is_done =
      execution?.is_done || !!(i.scheduler && i.scheduler.is_done);
    const has_started = !!(i.scheduler && i.scheduler.has_started);
    instances.push({
      ...i,
      routine,
      template,
      disabled,
      is_done,
      has_started,
    });
  });
  return instances;
});

const routines = computed(() => {
  const userId = fabriqStore.user?.id;
  if (!userId) return;
  const routines: Array<any> = [];
  routinesStore.collection
    .filter((r: Routine) => {
      return teams.value
        ?.filter((team) => r.teams.includes(team.id))
        .some((team) => !team.auditors.includes(userId));
    })
    .forEach((i: Routine) => {
      routines.push({
        ...i,
        disabled: !online.value,
      });
    });
  return routines;
});

const teams = computed(() => {
  const userId = fabriqStore.user?.id;
  if (!userId) return;
  const teams = [...teamsStore.collection];
  teams.sort((a: Team, b: Team) =>
    a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase())
  );
  return teams;
});

const stopScan = async () => {
  if (!scanning) return;
  scanning = false;
  document.body.style.display = "block";
  await BarcodeScanner.removeAllListeners();
  await BarcodeScanner.stopScan();
  barcodeUIBuilder.remove();
};

onIonViewDidEnter(() => loadAllData());
onIonViewDidLeave(() => (routinesLoaded = false));

const mixevts = (val: string) => {
  if (val === "scheduled") return "schedule tab";
  if (val === "favorite") return "favorites tab";
  return "all routines tab";
};
watch(filter, (val) => {
  mixpanelTracker.track(`open | ${mixevts(val)} | routine screen`);
  if (routinesLoaded) return;
  if (val === "scheduled") return;
  loadAllData();
});

watch(
  loading,
  (val) => {
    fabriqStore.$patch((state) => (state.hideTabs = !!val));
  },
  { immediate: true }
);

onDeactivated(() => {
  stopScan();
});

onBeforeUnmount(() => {
  stopScan();
});

const transformLegacyPermalinks = useTransformLegacyPermalinks();

const openBarcodeScanner = async () => {
  const status = await BarcodeScanner.checkPermissions();
  if (!status.camera) {
    const { camera } = await BarcodeScanner.requestPermissions();
    if (!camera) return false;
  }
  let loadingPromise: Promise<void> | null = null;
  if (!routinesLoaded) {
    loadingPromise = loadAllData();
  }
  scanning = true;

  // await BarcodeScanner.hideBackground();
  try {
    document.body.style.display = "none";
    barcodeUIBuilder.make(stopScan, t("common.qrcode"));
    mixpanelTracker.track("mb | open | QR code | routine tab");

    const listener = await BarcodeScanner.addListener(
      "barcodesScanned",
      async (result) => {
        if (result.barcodes.length > 0) {
          const barcode = result.barcodes[0];
          listener.remove();
          stopScan();
          const url = transformLegacyPermalinks(
            `${barcode.displayValue}?plain=true`
          );
          const options = ApiClient.getRequestOptions(url);
          let response: HttpResponse | null = null;
          try {
            response = await Http.get(options);

            if (!response) throw new Error("No response");
            if (!response.data) throw new Error("No data");
            if (response.status !== 200) throw new Error("Invalid status");
          } catch (e) {
            // if we get a lot of customer feedbacks, we could add a sentry call here.
            // for now, we don't want to spam sentry with this because we should already have entries from the server.
            const toast = await toastController.create({
              message: t("routines.errorLoadingRoutine"),
              duration: 2000,
              color: "danger",
            });
            toast.present();
            await localLogger.log(
              `Error loading routine from QR code: ${url} ${response?.status} ${
                response?.data
              } ${JSON.stringify(e)}`
            );
            fabriqStore.reportSentry(e as Error);
            return;
          }

          if (loadingPromise) await loadingPromise;
          const routineId = Number(
            ((response as HttpResponse).data as string).replace(
              /^.*routine=([0-9]+).*$/,
              "$1"
            )
          );
          const routine: Routine = routinesStore.collection.find(
            (r: any) => r.id === routineId
          );
          if (!routine) {
            const toast = await toastController.create({
              message: t("routines.waitLoading"),
              duration: 2000,
            });
            toast.present();
            return loadAllTeamRoutines();
          }

          if (!can.routine.startInstanceFromRoutineId(routineId)) {
            const toast = await toastController.create({
              message: t("routines.unauthorizedToStartInstance"),
              duration: 2000,
              color: "danger",
            });
            toast.present();
            return;
          }
          startRoutine(routine);
          mixpanelTracker.track("mb | launch | routine instance | qrcode");
        }
      }
    );

    await BarcodeScanner.startScan({
      formats: [BarcodeFormat.QrCode],
    });
  } catch (e) {
    console.error(e);
  }
};

const { users } = useUsers();
</script>

<template>
  <route-page
    :title="t('routine', 2)"
    class="routines-page"
    showPageRefreshButton
    fabIcon="qrcode"
    @refresh="loadAllData"
    @fab="openBarcodeScanner"
  >
    <!-- <route-page :title="t('routine', 2)" @refresh="loadAllData"> -->
    <template v-slot:toolbar>
      <ion-toolbar>
        <filters-bar :filters="filters" v-model="filter" />
      </ion-toolbar>
    </template>
    <div :class="{ 'ion-padding-top': filter === 'favorite' }">
      <div class="search-input" v-if="filter === 'routines' && !requests">
        <font-icon
          name="search"
          material
          size="1"
          color="var(--ion-color-primary-shade)"
        />
        <ion-input
          :placeholder="t(`common.search`)"
          enterkeyhint="search"
          v-model="search"
          @keypress.enter="$event.target.blur()"
        />
      </div>
      <instance-list
        class="margin-for-floating"
        v-if="filter === 'scheduled'"
        :hideLoader="hasEvLoader"
        :instances="instances"
        :users="users"
        @goto="startScheduledInstance"
        @reload="loadAllData"
      />
      <routine-list
        class="margin-for-floating"
        v-else
        :routines="routines"
        :teams="teams"
        :favorites="favorites"
        :routine-filter="search"
        :favorites-only="filter === 'favorite'"
        :users="users"
        :hideLoader="hasEvLoader"
        @goto="startRoutine"
        @favorite="toggleFavorite"
      />
    </div>
    <LoadingOverlay
      v-if="loading"
      :loadingText="loadingText"
      spinnerName="crescent"
    />
  </route-page>
</template>

<style scoped>
.search-input {
  margin: var(--f-padding-m) var(--ion-padding);
}
</style>
